import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { APP_CUSTOM_SCHEME } from "@src/mobile/utils/deepLinkHandler";
import { Platform } from "@src/mobile/utils/platform";
import { signInWithRedirect } from "aws-amplify/auth";

import { APP_V2_APP_EVENTS, logEvent } from "../../lib/analytics";
import { cognitoConfig } from "../cognito/config";
import { setActiveSsoProvider, SSO_PROVIDER } from "../Sso/activeProvider";
import { SSO_CALLBACK_PATH } from "./paths";

export const RIPPLING_SSO_REQUESTED_URL_KEY = "rippling_sso_requested_url";
export const SSO_PR_PREVIEW_KEY = "sso_pr_preview";

/** localStorage key for the PKCE code verifier used in the Capacitor OAuth flow. */
export const MOBILE_SSO_CODE_VERIFIER_KEY = "mobile_sso_code_verifier";

/**
 * Starts the Rippling SSO redirect flow. Called from the login form when
 * the email matches a non-aliased Clipboard Health employee pattern, and
 * from the SSO callback when retrying after account linking or IdP-initiated flows.
 *
 * On web, delegates to Amplify's signInWithRedirect which navigates the page.
 * On Capacitor, the WebView silently blocks page navigation, so we run the
 * OAuth PKCE flow ourselves and open it in an in-app browser.
 */
interface StartSsoRedirectOptions {
  ssoMode?: string;
  source?: string;
}

export async function startSsoRedirect(
  requestedUrl?: string,
  options?: StartSsoRedirectOptions
): Promise<void> {
  logEvent(APP_V2_APP_EVENTS.RIPPLING_SSO_STARTED, {
    ssoMode: options?.ssoMode,
    source: options?.source,
  });

  const isLoginPath =
    !requestedUrl || requestedUrl === "/enter" || requestedUrl === "/login" || requestedUrl === "/";
  // eslint-disable-next-line unicorn/prefer-ternary -- ternary is less readable for side-effect-only branches
  if (!isLoginPath) {
    localStorage.setItem(RIPPLING_SSO_REQUESTED_URL_KEY, requestedUrl);
  } else {
    localStorage.removeItem(RIPPLING_SSO_REQUESTED_URL_KEY);
  }

  storePrPreviewContext();

  setActiveSsoProvider(SSO_PROVIDER.RIPPLING);

  await (Platform.isMobile
    ? startSsoRedirectCapacitor()
    : signInWithRedirect({
        provider: { custom: "RipplingEmployees" },
        options: { prompt: "LOGIN" },
      }));
}

/**
 * Capacitor OAuth flow with PKCE.
 *
 * Amplify's signInWithRedirect can't navigate on Capacitor (WKWebView blocks
 * window.location mutations) and doesn't reliably store PKCE state when
 * navigation fails. We generate PKCE ourselves, build the Cognito authorize
 * URL, and open it in an in-app browser (SFSafariViewController / Chrome
 * Custom Tabs). The callback arrives as a deep link.
 */
async function startSsoRedirectCapacitor(): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(32);

  localStorage.setItem(MOBILE_SSO_CODE_VERIFIER_KEY, codeVerifier);

  const redirectUri = `${APP_CUSTOM_SCHEME}://${SSO_CALLBACK_PATH}`;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: cognitoConfig.clientId,
    redirect_uri: redirectUri,
    scope: "email openid profile",
    identity_provider: "RipplingEmployees",
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state,
    prompt: "LOGIN",
  });

  const oauthUrl = `https://${cognitoConfig.oauthDomain}/oauth2/authorize?${params.toString()}`;

  await openInBrowser({ url: oauthUrl });
}

// ---------------------------------------------------------------------------
// PKCE helpers (RFC 7636)
// Uses the Web Crypto API (not Node crypto) — available in all browsers and
// Capacitor WebViews with a secure origin. Falls back to a JS implementation
// for insecure contexts (HTTP live reload).
// ---------------------------------------------------------------------------

/* eslint-disable n/no-unsupported-features/node-builtins -- Web Crypto API, not Node crypto */

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(96);
  crypto.getRandomValues(array);
  return base64UrlEncode(array).slice(0, 128);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  if (!crypto.subtle) {
    throw new Error(
      "crypto.subtle is unavailable — PKCE requires a secure context (HTTPS). " +
        "Use a production Capacitor build or connect to the development environment."
    );
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/* eslint-enable n/no-unsupported-features/node-builtins */

// ---------------------------------------------------------------------------

/**
 * If the app is running in a PR preview (path starts with /pr-{number}/),
 * store the PR number so the root-level SSO callback handler can redirect
 * back to the correct preview build after Cognito returns.
 */
function storePrPreviewContext(): void {
  const prMatch = /^\/pr-(\d+)\//.exec(globalThis.location.pathname);
  if (prMatch) {
    localStorage.setItem(SSO_PR_PREVIEW_KEY, prMatch[1]);
  } else {
    localStorage.removeItem(SSO_PR_PREVIEW_KEY);
  }
}
